import { IFilterParams } from '@ag-grid-community/core';
import { Grid } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { ActionButton } from '@tassat/button';
import { AgGridFilterType } from '@tassat/common-definitions';
import React from 'react';

import { TableOptionsContext, handleSortChange, isColumnSorted } from 'SharedComponents/contexts/TableOptionsContext';

interface ServerSortProps extends
    React.HTMLAttributes<HTMLDivElement> {
    field: string;
    getMappedColumn?: (string) => string;
    handleSortChange?: (column, direction) => void;
    sortDirectionProp: 'ASC' | 'DESC' | 'active' | 'default' | false;
    setSortDirectionProp: React.Dispatch<React.SetStateAction<false | 'ASC' | 'DESC' | 'active' | 'default'>>;
    defaultSortField?: boolean;
    defaultSortDirection?: 'ASC' | 'DESC';
    isColumnSorted?: (params?) => 'ASC' | 'DESC' | 'active' | 'default' | false;
}

interface ServerSortFilterComponentProps extends
    IFilterParams,
    ServerSortProps,
    WithStyles<typeof muiStyles> {}

const muiStyles = () => ({
    root: { padding: '1rem' },
    button: { marginTop: '.5rem' }
});

export function ServerSort (props: ServerSortProps) {
    const {
        id,
        field,
        handleSortChange,
        sortDirectionProp,
        setSortDirectionProp,
        defaultSortField,
        defaultSortDirection
    } = props;

    const direction = sortDirectionProp;

    return (
        <Grid
            container
            direction='row'
            justifyContent='space-between'
            spacing={0}
            wrap='nowrap'
        >
            <ActionButton
                disabled={direction === 'ASC' || (direction === 'default' && defaultSortField && defaultSortDirection === 'ASC')}
                handleClick={(e) => {
                    setSortDirectionProp('ASC');
                    handleSortChange(field, 'ASC');
                }}
                id={`${id}_sortAscButton`}
                text={`A ${String.fromCharCode(0x2192)} Z`}
                type='muted'
            />
            <ActionButton
                disabled={direction === 'DESC' || (direction === 'default' && defaultSortField && defaultSortDirection === 'DESC')}
                handleClick={(e) => {
                    setSortDirectionProp('DESC');
                    handleSortChange(field, 'DESC');
                }}
                id={`${id}_sortDescButton`}
                text={`Z ${String.fromCharCode(0x2192)} A`}
                type='muted'
            />
        </Grid>
    );
}

const ServerSortFilterComponent = React.forwardRef((props: ServerSortFilterComponentProps, ref: any) => {
    const {
        classes,
        colDef,
        getMappedColumn,
        defaultSortField,
        defaultSortDirection
    } = props;

    const {
        TableOptions,
        TableOptionsDispatch
    } = React.useContext(TableOptionsContext);

    const checkAgainstTable = () => {
        const tableSort = isColumnSorted(colDef.field, getMappedColumn, TableOptions);
        if (tableSort !== sortDirection) {
            if (sortDirection === 'default' && defaultSortField && tableSort === defaultSortDirection) return 'default';
            setSortDirection(tableSort);

            return tableSort;
        }

        return sortDirection;
    };

    const inputRef = React.useRef<any>();
    const [ sortDirection, setSortDirection ] = React.useState<'ASC' | 'DESC' | 'active' | 'default' | false>(false);
    React.useEffect(() => {
        checkAgainstTable();
    }, []);

    React.useImperativeHandle(ref, () => {
        return {

            isFilterActive() {
                const tableCheck = checkAgainstTable();

                return tableCheck && tableCheck !== 'default';
            },

            afterGuiAttached: (params) => {
                checkAgainstTable();
            },

            doesFilterPass: () => {
                //Always true since server is doing the filtering
                return true;
            }
        };
    });

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            ref={inputRef}
            spacing={0}
        >
            <ServerSort
                defaultSortDirection={defaultSortDirection}
                defaultSortField={defaultSortField}
                field={colDef.field}
                handleSortChange={(field, direction) => handleSortChange(
                    field,
                    direction,
                    getMappedColumn,
                    TableOptions,
                    TableOptionsDispatch
                )}
                id={`${colDef.field}_serverSort`}
                isColumnSorted={(field) => isColumnSorted(field, getMappedColumn, TableOptions)}
                setSortDirectionProp={setSortDirection}
                sortDirectionProp={sortDirection}
            />
            <ActionButton
                className={classes.button}
                handleClick={(e) => {
                    setSortDirection('default');
                    handleSortChange(
                        colDef.field,
                        AgGridFilterType.ClearFilter,
                        getMappedColumn,
                        TableOptions,
                        TableOptionsDispatch
                    );
                }}
                id={`${colDef.field}_serverSort_clearFilter`}
                text='Clear'
                type='muted'
            />
        </Grid>
    );
});

ServerSortFilterComponent.displayName = 'ServerSortFilterComponent';

export default withStyles(muiStyles)(ServerSortFilterComponent);